import { useEffect, useRef, useState, useCallback } from "react";
import { debugLog } from "../utils/debugLog";

export function useAudioRecorder() {
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const [isRecording, setIsRecording] = useState(false);
  const [durationMs, setDurationMs] = useState(0);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  // Start recording
  const startRecording = useCallback(async () => {
    setError(null);

    if (isRecording) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });

      const mimeType =
        MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : "audio/ogg";

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      startTimeRef.current = Date.now();
      setDurationMs(0);

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        // Stop all tracks
        stream.getTracks().forEach((t) => t.stop());
      };

      mediaRecorder.start(10); // 10ms timeslice
      setIsRecording(true);
      
      debugLog('audio', 'Started recording', { mimeType });

      // Start timer
      timerRef.current = window.setInterval(() => {
        if (!startTimeRef.current) return;
        const now = Date.now();
        setDurationMs(now - startTimeRef.current);
      }, 200);
    } catch (err) {
      console.error("Failed to start audio recording", err);
      debugLog('audio', 'Recording failed to start', { error: err.message });
      setError("Could not access microphone. Please check permissions.");
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [isRecording]);

  // Stop and return blob + duration
  const stopRecording = useCallback(async () => {
    if (!mediaRecorderRef.current || !isRecording) return null;

    return new Promise((resolve) => {
      const mediaRecorder = mediaRecorderRef.current;

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mediaRecorder.mimeType });
        // Clear stream tracks
        if (mediaRecorder.stream) {
          mediaRecorder.stream.getTracks().forEach((t) => t.stop());
        }

        mediaRecorderRef.current = null;
        chunksRef.current = [];

        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }

        const finalDuration = Date.now() - (startTimeRef.current || Date.now());
        setDurationMs(finalDuration);
        setIsRecording(false);
        startTimeRef.current = null;

        // If too short, treat as cancelled
        if (finalDuration < 600) {
          debugLog('audio', 'Recording too short, cancelled', { durationMs: finalDuration });
          resolve(null);
        } else {
          debugLog('audio', 'Recording stopped', { durationMs: finalDuration });
          resolve({ blob, durationMs: finalDuration });
        }
      };

      mediaRecorder.stop();
    });
  }, [isRecording]);

  // Cancel recording without returning audio
  const cancelRecording = useCallback(() => {
    if (mediaRecorderRef.current) {
      try {
        mediaRecorderRef.current.stream
          ?.getTracks()
          ?.forEach((t) => t.stop());
      } catch (e) {
        // ignore
      }
    }
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    startTimeRef.current = null;

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    setIsRecording(false);
    setDurationMs(0);
  }, []);

  useEffect(() => {
    return () => {
      // cleanup on unmount
      cancelRecording();
    };
  }, [cancelRecording]);

  return {
    isRecording,
    durationMs,
    error,
    startRecording,
    stopRecording,
    cancelRecording,
  };
}
